import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import {
  Beer, Pizza, Coffee, IceCream, Sandwich, Wine, Cigarette, Droplets, Zap,
  Cookie, Soup, Cherry, UtensilsCrossed, Beef, Fish, Salad, Cake, Pill,
  Package, ShoppingBag, Candy, Apple, Milk, Croissant, Donut, Popcorn,
  Martini, GlassWater, CupSoda, Banana, Carrot, Egg, Ham, Drumstick, Flame,
  Loader2, CheckCircle2, XCircle
} from "lucide-react";
import type { Tables } from "@/integrations/supabase/types";
import { useUniqueValidation } from "@/hooks/useUniqueValidation";

type Category = Tables<"categories"> & { icon?: string | null; color?: string | null };

// Available icons for selection
const AVAILABLE_ICONS = [
  { name: "package", icon: Package, label: "Padrão" },
  { name: "beer", icon: Beer, label: "Cerveja" },
  { name: "pizza", icon: Pizza, label: "Pizza" },
  { name: "coffee", icon: Coffee, label: "Café" },
  { name: "ice-cream", icon: IceCream, label: "Sorvete" },
  { name: "sandwich", icon: Sandwich, label: "Lanche" },
  { name: "wine", icon: Wine, label: "Vinho" },
  { name: "cigarette", icon: Cigarette, label: "Cigarro" },
  { name: "droplets", icon: Droplets, label: "Bebidas" },
  { name: "zap", icon: Zap, label: "Energético" },
  { name: "cookie", icon: Cookie, label: "Doces" },
  { name: "soup", icon: Soup, label: "Sopa" },
  { name: "cherry", icon: Cherry, label: "Frutas" },
  { name: "utensils-crossed", icon: UtensilsCrossed, label: "Refeição" },
  { name: "beef", icon: Beef, label: "Carnes" },
  { name: "fish", icon: Fish, label: "Peixes" },
  { name: "salad", icon: Salad, label: "Saladas" },
  { name: "cake", icon: Cake, label: "Bolos" },
  { name: "pill", icon: Pill, label: "Farmácia" },
  { name: "shopping-bag", icon: ShoppingBag, label: "Compras" },
  { name: "candy", icon: Candy, label: "Balas" },
  { name: "apple", icon: Apple, label: "Maçã" },
  { name: "milk", icon: Milk, label: "Laticínios" },
  { name: "croissant", icon: Croissant, label: "Padaria" },
  { name: "donut", icon: Donut, label: "Rosquinha" },
  { name: "popcorn", icon: Popcorn, label: "Pipoca" },
  { name: "martini", icon: Martini, label: "Drinks" },
  { name: "glass-water", icon: GlassWater, label: "Água" },
  { name: "cup-soda", icon: CupSoda, label: "Refrigerante" },
  { name: "banana", icon: Banana, label: "Banana" },
  { name: "carrot", icon: Carrot, label: "Vegetais" },
  { name: "egg", icon: Egg, label: "Ovos" },
  { name: "ham", icon: Ham, label: "Frios" },
  { name: "drumstick", icon: Drumstick, label: "Frango" },
  { name: "flame", icon: Flame, label: "Churrasco" },
];

// Available colors for selection
const AVAILABLE_COLORS = [
  { name: "blue", label: "Azul", className: "bg-blue-500" },
  { name: "green", label: "Verde", className: "bg-green-500" },
  { name: "orange", label: "Laranja", className: "bg-orange-500" },
  { name: "purple", label: "Roxo", className: "bg-purple-500" },
  { name: "pink", label: "Rosa", className: "bg-pink-500" },
  { name: "cyan", label: "Ciano", className: "bg-cyan-500" },
  { name: "amber", label: "Âmbar", className: "bg-amber-500" },
  { name: "red", label: "Vermelho", className: "bg-red-500" },
  { name: "indigo", label: "Índigo", className: "bg-indigo-500" },
  { name: "teal", label: "Teal", className: "bg-teal-500" },
  { name: "lime", label: "Lima", className: "bg-lime-500" },
  { name: "rose", label: "Rosê", className: "bg-rose-500" },
];

const formSchema = z.object({
  name: z.string().min(1, "Nome é obrigatório").max(100, "Máximo 100 caracteres"),
  description: z.string().max(500, "Máximo 500 caracteres").optional(),
  icon: z.string().optional(),
  color: z.string().optional(),
  is_active: z.boolean(),
});

type FormData = z.infer<typeof formSchema>;

interface CategoryFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category?: Category | null;
  onSubmit: (data: FormData) => Promise<void>;
  isSubmitting?: boolean;
}

export function CategoryFormDialog({
  open,
  onOpenChange,
  category,
  onSubmit,
  isSubmitting,
}: CategoryFormDialogProps) {
  const isEditing = !!category;

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      icon: "package",
      color: "blue",
      is_active: true,
    },
  });

  const { isChecking, isUnique, checkUnique, reset: resetValidation } = useUniqueValidation({
    table: "categories",
    column: "name",
    excludeId: category?.id,
  });

  const watchedName = form.watch("name");

  useEffect(() => {
    if (category) {
      form.reset({
        name: category.name,
        description: category.description || "",
        icon: category.icon || "package",
        color: category.color || "blue",
        is_active: category.is_active ?? true,
      });
      resetValidation();
    } else {
      form.reset({
        name: "",
        description: "",
        icon: "package",
        color: "blue",
        is_active: true,
      });
      resetValidation();
    }
  }, [category, form, open, resetValidation]);

  // Check uniqueness when name changes
  useEffect(() => {
    if (!isEditing || watchedName.trim() !== category?.name) {
      checkUnique(watchedName);
    } else {
      resetValidation();
    }
  }, [watchedName, isEditing, category?.name, checkUnique, resetValidation]);

  const handleSubmit = async (data: FormData) => {
    if (isUnique === false) return; // Don't submit if duplicate
    await onSubmit(data);
    form.reset();
  };

  const selectedIcon = form.watch("icon");
  const selectedColor = form.watch("color");

  // Get selected icon component for preview
  const SelectedIconComponent = AVAILABLE_ICONS.find(i => i.name === selectedIcon)?.icon || Package;
  const selectedColorClass = AVAILABLE_COLORS.find(c => c.name === selectedColor)?.className || "bg-blue-500";

  const showValidation = watchedName.trim() && (!isEditing || watchedName.trim() !== category?.name);
  const isValid = !showValidation || isUnique === true;
  const canSubmit = !isSubmitting && !isChecking && isValid;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Editar Categoria" : "Nova Categoria"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            {/* Preview */}
            <div className="flex items-center justify-center p-4 rounded-lg border bg-muted/30">
              <div className={cn(
                "flex flex-col items-center justify-center rounded-xl p-4 min-w-[100px]",
                "bg-gradient-to-br border-2 transition-all",
                selectedColor === "blue" && "from-blue-500/20 to-blue-600/10 border-blue-500/50",
                selectedColor === "green" && "from-green-500/20 to-green-600/10 border-green-500/50",
                selectedColor === "orange" && "from-orange-500/20 to-orange-600/10 border-orange-500/50",
                selectedColor === "purple" && "from-purple-500/20 to-purple-600/10 border-purple-500/50",
                selectedColor === "pink" && "from-pink-500/20 to-pink-600/10 border-pink-500/50",
                selectedColor === "cyan" && "from-cyan-500/20 to-cyan-600/10 border-cyan-500/50",
                selectedColor === "amber" && "from-amber-500/20 to-amber-600/10 border-amber-500/50",
                selectedColor === "red" && "from-red-500/20 to-red-600/10 border-red-500/50",
                selectedColor === "indigo" && "from-indigo-500/20 to-indigo-600/10 border-indigo-500/50",
                selectedColor === "teal" && "from-teal-500/20 to-teal-600/10 border-teal-500/50",
                selectedColor === "lime" && "from-lime-500/20 to-lime-600/10 border-lime-500/50",
                selectedColor === "rose" && "from-rose-500/20 to-rose-600/10 border-rose-500/50",
              )}>
                <SelectedIconComponent className="h-8 w-8 mb-2 text-foreground/80" />
                <span className="font-semibold text-sm text-center">
                  {form.watch("name") || "Nome"}
                </span>
              </div>
            </div>

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input 
                        placeholder="Ex: Lanches" 
                        {...field} 
                        className={cn(
                          showValidation && isUnique === false && "border-destructive focus-visible:ring-destructive",
                          showValidation && isUnique === true && "border-green-500 focus-visible:ring-green-500"
                        )}
                      />
                      {showValidation && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {isChecking ? (
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                          ) : isUnique === true ? (
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                          ) : isUnique === false ? (
                            <XCircle className="h-4 w-4 text-destructive" />
                          ) : null}
                        </div>
                      )}
                    </div>
                  </FormControl>
                  {showValidation && isUnique === false ? (
                    <p className="text-sm text-destructive">
                      Já existe uma categoria com este nome
                    </p>
                  ) : (
                    <FormMessage />
                  )}
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição (opcional)</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Descreva a categoria..." 
                      className="resize-none"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Icon Selection */}
            <FormField
              control={form.control}
              name="icon"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ícone</FormLabel>
                  <ScrollArea className="h-[120px] rounded-lg border p-2">
                    <div className="grid grid-cols-7 gap-1.5">
                      {AVAILABLE_ICONS.map((iconOption) => {
                        const IconComponent = iconOption.icon;
                        return (
                          <button
                            key={iconOption.name}
                            type="button"
                            onClick={() => field.onChange(iconOption.name)}
                            className={cn(
                              "flex items-center justify-center p-2 rounded-lg transition-all",
                              "hover:bg-accent",
                              field.value === iconOption.name
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted/50"
                            )}
                            title={iconOption.label}
                          >
                            <IconComponent className="h-5 w-5" />
                          </button>
                        );
                      })}
                    </div>
                  </ScrollArea>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Color Selection */}
            <FormField
              control={form.control}
              name="color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cor</FormLabel>
                  <div className="flex flex-wrap gap-2">
                    {AVAILABLE_COLORS.map((colorOption) => (
                      <button
                        key={colorOption.name}
                        type="button"
                        onClick={() => field.onChange(colorOption.name)}
                        className={cn(
                          "w-8 h-8 rounded-full transition-all",
                          colorOption.className,
                          field.value === colorOption.name
                            ? "ring-2 ring-offset-2 ring-primary scale-110"
                            : "hover:scale-105"
                        )}
                        title={colorOption.label}
                      />
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Ativa</FormLabel>
                    <p className="text-sm text-muted-foreground">
                      Categorias inativas não aparecem no cardápio
                    </p>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={!canSubmit}>
                {isSubmitting ? "Salvando..." : isEditing ? "Salvar" : "Criar"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// Export icon and color maps for use in other components
export { AVAILABLE_ICONS, AVAILABLE_COLORS };
